import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer, Legend,
} from 'recharts';
import { getProfile } from '../services/api';
import ArchetypeBadge from './ArchetypeBadge';
import { ARCHETYPE_META } from './NeuralProfile';

const ALL_DIMENSION_LABELS = {
  rawSpeed:         'Raw Speed',
  decisionQuality:  'Decision Quality',
  aimPrecision:     'Aim Precision',
  trackingAccuracy: 'Tracking Accuracy',
  workingMemory:    'Working Memory',
  flickSpeed:       'Flick Speed',
  prioritization:   'Prioritization',
  consistency:      'Consistency',
};

const GENRE_DIMENSION_KEYS = {
  general: ['rawSpeed', 'decisionQuality', 'aimPrecision', 'trackingAccuracy', 'workingMemory', 'consistency'],
  fps:     ['rawSpeed', 'decisionQuality', 'aimPrecision', 'trackingAccuracy', 'flickSpeed', 'consistency'],
  moba:    ['rawSpeed', 'decisionQuality', 'prioritization', 'trackingAccuracy', 'workingMemory', 'consistency'],
};

function getDimensionKeys(profileA, profileB) {
  const genreA = profileA.genre || 'general';
  const genreB = profileB.genre || 'general';
  if (genreA === genreB) return GENRE_DIMENSION_KEYS[genreA] || GENRE_DIMENSION_KEYS.general;
  // Cross-genre: union of both, preserving a sensible display order
  const keysA = GENRE_DIMENSION_KEYS[genreA] || GENRE_DIMENSION_KEYS.general;
  const keysB = GENRE_DIMENSION_KEYS[genreB] || GENRE_DIMENSION_KEYS.general;
  return [...new Set([...keysA, ...keysB])];
}

function getOverall(scores) {
  const vals = Object.values(scores).filter((v) => typeof v === 'number');
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

function WinIndicator({ a, b }) {
  if (a === b) return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>TIE</span>;
  return a > b
    ? <span style={{ color: '#10b981', fontSize: 11, fontWeight: 700 }}>▲</span>
    : <span style={{ color: '#ef4444', fontSize: 11, fontWeight: 700 }}>▼</span>;
}

export default function CompareView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const idA = searchParams.get('a');
  const idB = searchParams.get('b');

  const [profileA, setProfileA] = useState(null);
  const [profileB, setProfileB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Compare Profiles | NeuroRank';
    import('../services/track').then(({ track }) => track('compare_view', { a: idA, b: idB }));
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, []); // eslint-disable-line
  useEffect(() => {
    if (!idA || !idB) {
      setError('Two profile IDs are required (a= and b=).');
      setLoading(false);
      return;
    }
    Promise.all([
      getProfile(idA).catch(() => null),
      getProfile(idB).catch(() => null),
    ]).then(([a, b]) => {
      if (!a || !b) {
        setError('One or both profiles could not be found.');
      } else {
        setProfileA(a);
        setProfileB(b);
      }
    }).finally(() => setLoading(false));
  }, [idA, idB]);

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: 80, textAlign: 'center' }}>
        <p className="text-muted">Loading profiles...</p>
      </div>
    );
  }

  if (error || !profileA || !profileB) {
    return (
      <div className="container" style={{ paddingTop: 80, textAlign: 'center' }}>
        <div className="card" style={{ maxWidth: 440, margin: '0 auto' }}>
          <h2 style={{ marginBottom: 8 }}>Comparison Error</h2>
          <p className="text-muted" style={{ marginBottom: 20 }}>{error}</p>
          <button className="btn btn--ghost" onClick={() => navigate('/scout')}>
            Scout Dashboard
          </button>
        </div>
      </div>
    );
  }

  const metaA = ARCHETYPE_META[profileA.archetype.id] || ARCHETYPE_META.BALANCED;
  const metaB = ARCHETYPE_META[profileB.archetype.id] || ARCHETYPE_META.BALANCED;

  const dimKeys = getDimensionKeys(profileA, profileB);

  const radarData = dimKeys.map((key) => ({
    dimension: ALL_DIMENSION_LABELS[key] || key,
    [profileA.playerTag]: profileA.scores[key] || 0,
    [profileB.playerTag]: profileB.scores[key] || 0,
  }));

  const overallA = getOverall(profileA.scores);
  const overallB = getOverall(profileB.scores);

  // Dimension wins tally
  const winsA = dimKeys.filter((k) => (profileA.scores[k] || 0) > (profileB.scores[k] || 0)).length;
  const winsB = dimKeys.filter((k) => (profileB.scores[k] || 0) > (profileA.scores[k] || 0)).length;
  const ties = dimKeys.length - winsA - winsB;

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid var(--border)', padding: '32px 0 28px' }}>
        <div className="container">
          <p style={{ fontSize: 11, letterSpacing: 3, color: 'var(--accent)', marginBottom: 20, fontWeight: 700, textAlign: 'center' }}>
            HEAD TO HEAD COMPARISON
          </p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 32, flexWrap: 'wrap' }}>
            {/* Player A */}
            <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 24, color: metaA.color }}>{profileA.playerTag}</h2>
              <ArchetypeBadge archetypeId={profileA.archetype.id} archetypeName={profileA.archetype.name} />
            </div>

            {/* VS + wins tally */}
            <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: 'var(--text-muted)', fontWeight: 700, marginBottom: 6 }}>DIMENSIONS WON</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 28, fontWeight: 900, color: winsA > winsB ? metaA.color : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>{winsA}</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 700 }}>VS</span>
                <span style={{ fontSize: 28, fontWeight: 900, color: winsB > winsA ? metaB.color : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>{winsB}</span>
              </div>
              {ties > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{ties} tied</div>}
            </div>

            {/* Player B */}
            <div style={{ textAlign: 'left', flex: '0 0 auto' }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 24, color: metaB.color }}>{profileB.playerTag}</h2>
              <ArchetypeBadge archetypeId={profileB.archetype.id} archetypeName={profileB.archetype.name} />
            </div>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop: 32 }}>
        {/* Overall score banner */}
        <motion.div
          className="card"
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', textAlign: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}
        >
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 2, fontWeight: 600 }}>OVERALL</div>
            <div style={{ fontSize: 52, fontWeight: 900, color: overallA >= overallB ? metaA.color : 'var(--text-muted)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{overallA}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>{profileA.playerTag}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: 13, fontWeight: 800, padding: '6px 16px', borderRadius: 20,
              background: overallA === overallB ? 'var(--bg-elevated)' : overallA > overallB ? `${metaA.color}18` : `${metaB.color}18`,
              color: overallA === overallB ? 'var(--text-muted)' : overallA > overallB ? metaA.color : metaB.color,
              border: `1px solid ${overallA === overallB ? 'var(--border)' : overallA > overallB ? `${metaA.color}30` : `${metaB.color}30`}`,
            }}>
              {overallA > overallB ? `+${overallA - overallB} pts` :
               overallB > overallA ? `+${overallB - overallA} pts` : 'TIE'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
              {overallA > overallB ? `${profileA.playerTag} leads` :
               overallB > overallA ? `${profileB.playerTag} leads` : 'Even match'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 2, fontWeight: 600 }}>OVERALL</div>
            <div style={{ fontSize: 52, fontWeight: 900, color: overallB >= overallA ? metaB.color : 'var(--text-muted)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{overallB}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>{profileB.playerTag}</div>
          </div>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* Overlaid radar chart */}
          <motion.div
            className="card"
            initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h3 style={{ marginBottom: 20 }}>Neural Radar</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#1f2937" />
                <PolarAngleAxis dataKey="dimension" tick={{ fill: '#8b9ab5', fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#4b5a72', fontSize: 10 }} />
                <Radar
                  name={profileA.playerTag}
                  dataKey={profileA.playerTag}
                  stroke={metaA.color}
                  fill={metaA.color}
                  fillOpacity={0.15}
                  strokeWidth={2}
                  isAnimationActive
                  animationBegin={200}
                />
                <Radar
                  name={profileB.playerTag}
                  dataKey={profileB.playerTag}
                  stroke={metaB.color}
                  fill={metaB.color}
                  fillOpacity={0.15}
                  strokeWidth={2}
                  isAnimationActive
                  animationBegin={400}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </RadarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Dimension-by-dimension breakdown */}
          <motion.div
            className="card"
            initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h3 style={{ marginBottom: 20 }}>Dimension Breakdown</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', padding: '4px 0', fontWeight: 600 }}>DIMENSION</th>
                  <th style={{ textAlign: 'center', fontSize: 11, color: metaA.color, padding: '4px 8px', fontWeight: 700, maxWidth: 80, overflow: 'hidden' }}>{profileA.playerTag}</th>
                  <th style={{ width: 24 }} />
                  <th style={{ textAlign: 'center', fontSize: 11, color: metaB.color, padding: '4px 8px', fontWeight: 700, maxWidth: 80, overflow: 'hidden' }}>{profileB.playerTag}</th>
                </tr>
              </thead>
              <tbody>
                {dimKeys.map((key, i) => {
                  const label = ALL_DIMENSION_LABELS[key] || key;
                  const a = profileA.scores[key] || 0;
                  const b = profileB.scores[key] || 0;
                  const aOnly = a > 0 && b === 0;
                  const bOnly = b > 0 && a === 0;
                  return (
                    <motion.tr
                      key={key}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 + i * 0.06 }}
                      style={{ borderBottom: '1px solid var(--border)' }}
                    >
                      <td style={{ padding: '10px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                        {label}
                        {(aOnly || bOnly) && (
                          <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 6 }}>
                            ({aOnly ? profileA.genre : profileB.genre} only)
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 800, fontSize: 16, color: a >= b && a > 0 ? metaA.color : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                        {a > 0 ? a : <span style={{ fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 4px' }}>
                        <WinIndicator a={a} b={b} />
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 800, fontSize: 16, color: b >= a && b > 0 ? metaB.color : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                        {b > 0 ? b : <span style={{ fontSize: 12 }}>—</span>}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>
        </div>

        {/* Action row */}
        <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn--ghost" onClick={() => navigate(`/profile/${idA}`)}>
            View {profileA.playerTag}'s Profile
          </button>
          <button className="btn btn--ghost" onClick={() => navigate(`/profile/${idB}`)}>
            View {profileB.playerTag}'s Profile
          </button>
          <button className="btn btn--ghost" onClick={() => navigate('/scout')}>
            Back to Scout Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
